import { DocumentNode } from 'graphql'
import {
  ApolloClient,
  ObservableQuery,
  NormalizedCacheObject,
} from '@apollo/client'

import { localStorageKeys } from '@sketch/constants'
import { getParsedItem, setStringifiedItem } from '@sketch/utils'

type StorageKey = keyof typeof localStorageKeys
type StorageValue = (typeof localStorageKeys)[StorageKey]

type Apollo = ApolloClient<NormalizedCacheObject>

const queueStorageUpdate = (storageValue: StorageValue, data: any) => {
  setTimeout(() => setStringifiedItem(storageValue, data))
}

export const createStoragePersist = (apolloClient: Apollo) => {
  const storage = new WeakMap<DocumentNode, [StorageValue, ObservableQuery]>()

  const syncQueryWithStorage = (
    query: DocumentNode,
    storageKey: StorageKey
  ) => {
    const observableItem = storage.get(query) || []
    const storageValue = localStorageKeys[storageKey]

    const savedStorageValue = observableItem[0]
    let observable = observableItem[1]

    /* if no "observable" means it's the first time the listener is set */
    if (!observable) {
      const previousSavedQuery = getParsedItem(storageValue)

      if (previousSavedQuery) {
        apolloClient.writeQuery({
          query,
          data: previousSavedQuery,
        })
      }

      observable = apolloClient.watchQuery({
        query,
        fetchPolicy: 'cache-only',
      })
    }

    /* We are already subscribed to these updates (same key, same query) */
    if (savedStorageValue === storageValue) {
      return
    }

    observable.subscribe(({ data, partial }) => {
      if (partial) {
        // watchQuery emits partial results (empty object) on init when there is no data in cache,
        // which causes incomplete data to be stored in localStorage
        // and leads to errors when rehydrating the cache on next load
        // Fixes https://linear.app/sketch/issue/SWEB-2218/apollo-error-when-visiting-signin-and-signup
        return
      }
      queueStorageUpdate(storageValue, data)
    })

    storage.set(query, [storageValue, observable])
  }

  return {
    syncQueryWithStorage,
  }
}
